import { useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Form, Input, Link, Space, Table, Tag, Tooltip } from 'tdesign-react';
import { isVoid, renderDateTime } from '@/utils/common';
import { getHostList } from '@/services/resourceService';
import useTable from '@/hooks/useTable';
import SvgIcon from '@/components/SvgIcon';
import NoData from '@/components/NoData';
import { fetchQuickFilterList } from '@/slices/resourceSlice';

const { FormItem } = Form;

const lv1Options = [
  { label: '全部主机', value: 'all' },
  { label: '未部署', value: 'no-deploy' },
];

export const defaultColumns = [
  {
    colKey: 'row-select',
    type: 'multiple',
    fixed: 'left',
    checkProps: ({ row }) => ({ disabled: row['row-select'] === false }),
    width: 46,
  },
  {
    colKey: 'hostName',
    title: '所属集群',
    width: 160,
  },
  {
    colKey: 'hostIp',
    title: 'IP地址',
    width: 160,
  },
  {
    colKey: 'serviceIp',
    title: '服务地址',
    cell: ({ row }) => row.serviceIp || row.hostIp,
    width: 160,
  },
  {
    colKey: 'hostPort',
    title: 'ssh端口',
    width: 100,
  },
  {
    colKey: 'hostUser',
    title: 'ssh用户',
    width: 140,
  },
  {
    title: 'ssh信任状态',
    colKey: 'isVerified',
    cell: ({ row }) =>
      row.isVerified === 2 ? (
        <Tag theme='success' variant='outline' size='small'>
          已验证
        </Tag>
      ) : (
        <Tag theme='danger' variant='outline' size='small'>
          未验证
        </Tag>
      ),
    width: 140,
  },
  {
    colKey: 'desc',
    title: '备注',
    width: 180,
  },
  {
    colKey: 'ip',
    title: '机器上的所有IP',
    cell: ({ row }) => (
      <>
        {row.publicIpAddress && (
          <TBlock>
            <Tag style={{ color: '#333', backgroundColor: '#ffe7ba', marginRight: 4 }} size='small'>
              公
            </Tag>
            {row.publicIpAddress}
          </TBlock>
        )}
        {row.privateIpAddress && (
          <TBlock>
            <Tag style={{ color: '#333', backgroundColor: '#bae7ff', marginRight: 4 }} size='small'>
              内
            </Tag>
            {row.privateIpAddress}
          </TBlock>
        )}
      </>
    ),
    width: 160,
  },
  {
    colKey: 'components',
    title: '机器上的组件',
    cell: ({ row }) => (
      <Space size='small' breakLine>
        {row.components?.split(',').map((item) => (
          <Tag size='small' key={item}>
            {item}
          </Tag>
        ))}
      </Space>
    ),
    width: 300,
  },
  {
    title: '配置信息',
    colKey: 'modelName',
    cell: ({ row }) => {
      const cpuInfo = JSON.parse(row.cpuInfo || '{}');
      const memoryInfo = JSON.parse(row.memoryInfo || '{}');
      let tip;
      if (cpuInfo.modelName) {
        tip = (
          <Tooltip theme='light' content={cpuInfo.modelName}>
            <Link hover='color'>
              <SvgIcon
                type='chip'
                style={{ width: 16, height: 16, color: 'var(--td-font-gray-2)' }}
              />
            </Link>
          </Tooltip>
        );
      }
      return (
        <Space align='center' size='small'>
          {cpuInfo?.processorNumber && (
            <TBlock>
              {cpuInfo.processorNumber}核{tip}
            </TBlock>
          )}
          {memoryInfo?.memorySize && <span>{memoryInfo.memorySize}GB</span>}
        </Space>
      );
    },
    width: 140,
  },
  {
    title: '创建时间',
    colKey: 'createTime',
    cell: renderDateTime,
    width: 180,
  },
  {
    title: '更新时间',
    colKey: 'updateTime',
    cell: renderDateTime,
    width: 180,
  },
];

function HostTable({
  clusterId,
  filters,
  operators,
  fetchQuickFilterList,
  selectOnRowClick,
  afterLoadData, // 数据加载完后
  selectedRowData,
  onSelectChange,
  columns,
  refreshEmmiter,
}) {
  const [currentLv1, setCurrentLv1] = useState('all');
  const [currentCmp, setCurrentCmp] = useState([]);

  const selectedRowKeys = selectedRowData.map((item) => item.id);

  const loadData = useMemoizedFn(async (params) => {
    if (isVoid(clusterId)) {
      return;
    }
    // api分页无效，暂认为假分页
    const _params = {
      ...params,
      opsClusterId: clusterId,
      // pageSize: 99999,
      // startIndex: 1,
    };
    if (currentLv1 === 'all') {
      _params.all = 1;
    }

    if (!isEmpty(currentCmp)) {
      _params.opsComponentId = currentCmp.join(',');
    }
    const { data } = await getHostList(_params);
    let { items } = data;
    // 二次处理数据
    if (afterLoadData) {
      items = afterLoadData(items);
    }
    setData(items, data.count);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns: columns || defaultColumns,
    search: (
      <FormItem name='keyword'>
        <Input clearable placeholder='输入名称/IP检索' />
      </FormItem>
    ),
    operators,
    onSearch: loadData,
    remote: true,
  });

  if (refreshEmmiter) {
    refreshEmmiter.useSubscription(() => {
      doRefresh();
    });
  }

  useEffect(() => {
    doSearch();
  }, [doSearch, clusterId]);

  useEffect(() => {
    if (clusterId) {
      fetchQuickFilterList({ clusterId });
    }
  }, [clusterId, fetchQuickFilterList]);

  const onSelect = (val) => {
    setCurrentLv1(val);
    // 忽略无变化
    if (currentLv1 !== val) {
      setCurrentCmp([]);
      setTimeout(() => doSearch(), 30);
    }
  };

  const onSelectCmp = (val) => {
    const found = currentCmp.find((item) => item === val);
    let changed;
    if (found) {
      // 剔除
      changed = currentCmp.filter((item) => item !== val).slice();
    } else {
      changed = [...currentCmp, val];
    }
    setCurrentLv1(null);
    setCurrentCmp(changed);
    if (isEmpty(changed)) {
      setCurrentLv1('all');
    }
    setTimeout(() => doSearch(), 30);
  };

  return isVoid(clusterId) ? (
    <Root>
      <NoData style={{ width: '100%', marginTop: 140 }} tip='请先选择左侧集群' />
    </Root>
  ) : (
    <Root>
      {header}
      <QuickFilter size='small' breakLine align='center'>
        <div>快速筛选：</div>
        {lv1Options.map((item, index) => (
          <Button
            key={index}
            size='small'
            theme='primary'
            variant={currentLv1 === item.value ? 'outline' : 'text'}
            onClick={() => onSelect(item.value)}
          >
            {item.label}
          </Button>
        ))}

        {filters.map((item, index) => (
          <Button
            key={`${item.label}/${index}`}
            size='small'
            theme='primary'
            variant={currentCmp.includes(item.value) ? 'outline' : 'text'}
            onClick={() => onSelectCmp(item.value)}
          >
            {item.label}
          </Button>
        ))}
      </QuickFilter>
      <Table
        {...config}
        rowKey='id'
        selectOnRowClick={selectOnRowClick}
        selectedRowKeys={selectedRowKeys}
        onSelectChange={onSelectChange}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    filters: state.resource.filters,
  }),
  {
    fetchQuickFilterList,
  },
)(HostTable);

const Root = styled.div`
  flex: 1;
  min-width: 0;
  border-left: 2px solid #edeff2;
  margin-left: -2px;
  padding: 24px;
`;

const TBlock = styled.div`
  display: inline-flex;
  line-height: 20px;
`;

const QuickFilter = styled(Space)`
  font-size: 12px;
  margin-bottom: 12px;
  .t-button--variant-text.t-button--theme-primary {
    color: var(--td-font-gray-2);
  }
`;
